import React, { useState, useEffect, useCallback } from 'react';
import {
  BookOpen, Plus, Edit2, Trash2, Eye, EyeOff, Save, X,
  Tag, Monitor, ChevronDown, Search, RefreshCw, CheckCircle2, Circle
} from 'lucide-react';
import { API, authFetch } from '../lib/api';
import { erpToast } from '../components/erp/ERPToast';

// ─── Screen list: pulled from NAV_ITEMS ids ──────────────────────────────────
const SCREENS = [
  { id: 'dashboard',           label: 'Dashboard' },
  { id: 'licenses',            label: 'License Generator' },
  { id: 'licensor',            label: 'Licensor Console' },
  { id: 'license-registry',    label: 'License Registry' },
  { id: 'customers-360',       label: 'Customers 360' },
  { id: 'renewal-manager',     label: 'Renewal Manager' },
  { id: 'payments',            label: 'Payments' },
  { id: 'signup-metadata',     label: 'Signup Metadata' },
  { id: 'sub-users',           label: 'Sub Users' },
  { id: 'pricing-tiers',       label: 'License Pricing Manager' },
  { id: 'industry-matrix',     label: 'Industry Matrix' },
  { id: 'template-db-setup',   label: 'Template DB Setup' },
  { id: 'module-privileges',   label: 'Module Privileges' },
  { id: 'tier-variations',     label: 'Tier Variations' },
  { id: 'industry-master',     label: 'Industry Master' },
  { id: 'active-sessions',     label: 'Active Sessions' },
  { id: 'translations',        label: 'Translations / i18n' },
  { id: 'schema-relations',    label: 'Schema & Relations' },
  { id: 'audit',               label: 'Audit Vault' },
  { id: 'users',               label: 'Users & Roles' },
  { id: 'pool-config',         label: 'Pool Config' },
  { id: 'reports',             label: 'Reports' },
  { id: 'keysettings',         label: 'Key Settings' },
  { id: 'help-kb',             label: 'Help KB (this page)' },
  { id: 'country-config',      label: 'Country Configuration' },
  { id: 'general',             label: 'General / Cross-cutting' },
];
const screenLabel = id => SCREENS.find(s => s.id === id)?.label || id;

const CATEGORIES = ['General', 'Getting Started', 'Configuration', 'How To', 'Troubleshooting', 'Reference', 'FAQ', 'Release Notes'];

const EMPTY = {
  screenId: 'general', screenLabel: 'General', title: '',
  content: '', contentType: 'markdown', category: 'General',
  tags: [], sortOrder: 0, isPublished: false
};

// ─── Tiny markdown preview (bold, italic, code, headings, bullets) ───────────
function MiniPreview({ text }) {
  const html = text
    .replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;')
    .replace(/^### (.+)$/gm, '<h3 style="font-size:1rem;font-weight:700;margin:8px 0 4px">$1</h3>')
    .replace(/^## (.+)$/gm,  '<h2 style="font-size:1.1rem;font-weight:700;margin:10px 0 4px">$1</h2>')
    .replace(/^# (.+)$/gm,   '<h1 style="font-size:1.2rem;font-weight:700;margin:12px 0 4px">$1</h1>')
    .replace(/\*\*(.+?)\*\*/g,'<strong>$1</strong>')
    .replace(/\*(.+?)\*/g,   '<em>$1</em>')
    .replace(/`(.+?)`/g,     '<code style="background:#f1f5f9;padding:2px 5px;border-radius:3px;font-family:monospace;font-size:0.85em">$1</code>')
    .replace(/^- (.+)$/gm,   '<li style="margin:2px 0">$1</li>')
    .replace(/\n/g, '<br/>');
  return <div style={{ fontFamily: 'inherit', fontSize: '0.86rem', lineHeight: 1.7, color: '#334155' }} dangerouslySetInnerHTML={{ __html: html }} />;
}

export default function HelpKBPage() {
  const [articles, setArticles] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [search,   setSearch]   = useState('');
  const [filterScreen, setFilterScreen] = useState('ALL');
  const [filterPub,    setFilterPub]    = useState('ALL'); // ALL | published | draft
  const [showForm, setShowForm] = useState(false);
  const [editing,  setEditing]  = useState(null);  // articleId | null (null = new)
  const [form,     setForm]     = useState(EMPTY);
  const [saving,   setSaving]   = useState(false);
  const [preview,  setPreview]  = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await authFetch(`${API}/help-kb`);
      const d = await r.json();
      setArticles(Array.isArray(d) ? d : []);
    } catch (e) { erpToast.error(e.message); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = articles.filter(a => {
    if (filterScreen !== 'ALL' && a.screenId !== filterScreen) return false;
    if (filterPub === 'published' && !a.isPublished) return false;
    if (filterPub === 'draft'     &&  a.isPublished) return false;
    if (search) {
      const q = search.toLowerCase();
      return (a.title||'').toLowerCase().includes(q)
        || (a.content||'').toLowerCase().includes(q)
        || (a.category||'').toLowerCase().includes(q)
        || (a.tags||[]).some(t => t.toLowerCase().includes(q));
    }
    return true;
  });

  const openNew = () => {
    setEditing(null);
    setForm({ ...EMPTY });
    setPreview(false);
    setShowForm(true);
  };

  const openEdit = (a) => {
    setEditing(a.articleId);
    setForm({
      screenId: a.screenId, screenLabel: a.screenLabel, title: a.title,
      content: a.content, contentType: a.contentType, category: a.category,
      tags: a.tags || [], sortOrder: a.sortOrder, isPublished: a.isPublished
    });
    setPreview(false);
    setShowForm(true);
  };

  const save = async () => {
    if (!form.title.trim()) { erpToast.error('Title is required'); return; }
    setSaving(true);
    try {
      const payload = { ...form, screenLabel: screenLabel(form.screenId) };
      const url    = editing ? `${API}/help-kb/${editing}` : `${API}/help-kb`;
      const method = editing ? 'PUT' : 'POST';
      const r = await authFetch(url, { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      const d = await r.json();
      if (d.error) throw new Error(d.error);
      erpToast.success(editing ? 'Article updated' : 'Article created');
      setShowForm(false);
      setEditing(null);
      load();
    } catch (e) { erpToast.error(e.message); }
    setSaving(false);
  };

  const del = async (id, title) => {
    if (!window.confirm(`Delete article "${title}"?`)) return;
    try {
      await authFetch(`${API}/help-kb/${id}`, { method: 'DELETE' });
      erpToast.success('Deleted');
      setArticles(p => p.filter(a => a.articleId !== id));
    } catch (e) { erpToast.error(e.message); }
  };

  const togglePublish = async (a) => {
    try {
      await authFetch(`${API}/help-kb/${a.articleId}/publish`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ isPublished: !a.isPublished })
      });
      setArticles(p => p.map(x => x.articleId === a.articleId ? { ...x, isPublished: !x.isPublished } : x));
      erpToast.success(a.isPublished ? 'Unpublished' : 'Published');
    } catch (e) { erpToast.error(e.message); }
  };

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: 'var(--surface-1,#f8fafc)', overflow: 'hidden' }}>

      {/* ── Header ──────────────────────────────────────────────────────── */}
      <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border,#e2e8f0)', background: 'var(--surface-0,#fff)', display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
        <BookOpen size={18} color="#3b82f6" />
        <div>
          <div style={{ fontWeight: 800, fontSize: '1rem', color: '#0f172a' }}>Help Knowledge Base</div>
          <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{articles.length} articles across {new Set(articles.map(a => a.screenId)).size} screens</div>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <button className="erp-btn" onClick={load} style={{ fontSize: '0.79rem' }} data-testid="help-kb-refresh">
            <RefreshCw size={12} /> Refresh
          </button>
          <button
            className="erp-btn erp-btn-primary"
            onClick={openNew}
            style={{ fontSize: '0.79rem', background: '#2563eb', color: '#fff', border: 'none' }}
            data-testid="help-kb-new"
          >
            <Plus size={13} /> New Article
          </button>
        </div>
      </div>

      {/* ── Filters ─────────────────────────────────────────────────────── */}
      <div style={{ padding: '8px 16px', borderBottom: '1px solid var(--border,#e2e8f0)', background: '#f8fafc', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', flexShrink: 0 }}>
        {/* search */}
        <div style={{ position: 'relative', minWidth: 220 }}>
          <Search size={12} color="#94a3b8" style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)' }} />
          <input
            className="form-input"
            placeholder="Search title, content, tags…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ paddingLeft: 26, fontSize: '0.79rem' }}
            data-testid="help-kb-search"
          />
        </div>

        {/* screen filter */}
        <div style={{ position: 'relative' }}>
          <Monitor size={12} color="#94a3b8" style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)' }} />
          <select
            className="form-select"
            value={filterScreen}
            onChange={e => setFilterScreen(e.target.value)}
            style={{ paddingLeft: 26, fontSize: '0.79rem', minWidth: 200 }}
            data-testid="help-kb-filter-screen"
          >
            <option value="ALL">All Screens</option>
            {SCREENS.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
          <ChevronDown size={12} color="#94a3b8" style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
        </div>

        {/* status filter */}
        {['ALL', 'published', 'draft'].map(v => (
          <button
            key={v}
            onClick={() => setFilterPub(v)}
            style={{
              fontSize: '0.75rem', padding: '4px 12px', borderRadius: 20,
              border: filterPub === v ? '1.5px solid #3b82f6' : '1px solid #e2e8f0',
              background: filterPub === v ? '#dbeafe' : '#fff',
              color: filterPub === v ? '#1d4ed8' : '#64748b',
              cursor: 'pointer', fontWeight: filterPub === v ? 700 : 500,
            }}
          >
            {v === 'ALL' ? 'All' : v.charAt(0).toUpperCase() + v.slice(1)}
          </button>
        ))}

        <span style={{ fontSize: '0.75rem', color: '#94a3b8', marginLeft: 'auto' }}>
          {filtered.length} of {articles.length} articles
        </span>
      </div>

      {/* ── Article Grid ────────────────────────────────────────────────── */}
      <div style={{ flex: 1, overflow: 'auto', padding: 16 }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 60, color: '#94a3b8', fontSize: '0.86rem' }}>Loading articles…</div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 60, color: '#94a3b8' }}>
            <BookOpen size={32} style={{ marginBottom: 12, opacity: 0.4 }} />
            <div style={{ fontSize: '0.86rem' }}>No articles yet for this filter.</div>
            <button className="erp-btn erp-btn-primary" onClick={openNew} style={{ marginTop: 16, background: '#2563eb', color: '#fff', border: 'none' }}>
              <Plus size={13} /> Create First Article
            </button>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 12 }}>
            {filtered.map(a => (
              <ArticleCard
                key={a.articleId}
                article={a}
                onEdit={() => openEdit(a)}
                onDelete={() => del(a.articleId, a.title)}
                onTogglePublish={() => togglePublish(a)}
              />
            ))}
          </div>
        )}
      </div>

      {/* ── Article Form Modal ──────────────────────────────────────────── */}
      {showForm && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20
        }}>
          <div style={{
            background: '#fff', borderRadius: 12, width: '100%', maxWidth: 780,
            maxHeight: '92vh', display: 'flex', flexDirection: 'column',
            boxShadow: '0 25px 60px rgba(0,0,0,0.3)'
          }}>
            {/* form header */}
            <div style={{ padding: '14px 20px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
              <BookOpen size={16} color="#3b82f6" />
              <span style={{ fontWeight: 700, fontSize: '0.95rem', color: '#0f172a' }}>
                {editing ? 'Edit Article' : 'New Article'}
              </span>
              <button onClick={() => { setShowForm(false); setEditing(null); }} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8' }}>
                <X size={18} />
              </button>
            </div>

            {/* form body */}
            <div style={{ flex: 1, overflow: 'auto', padding: 20, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              {/* screen link */}
              <div>
                <label className="form-label">Linked Screen <span style={{ color: '#ef4444' }}>*</span></label>
                <div style={{ position: 'relative' }}>
                  <Monitor size={12} color="#94a3b8" style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)' }} />
                  <select className="form-select" value={form.screenId} onChange={e => f('screenId', e.target.value)} style={{ paddingLeft: 26 }} data-testid="help-kb-form-screen">
                    {SCREENS.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
                  </select>
                </div>
              </div>

              {/* category */}
              <div>
                <label className="form-label">Category</label>
                <select className="form-select" value={form.category} onChange={e => f('category', e.target.value)} data-testid="help-kb-form-category">
                  {CATEGORIES.map(c => <option key={c}>{c}</option>)}
                </select>
              </div>

              {/* title full width */}
              <div style={{ gridColumn: '1 / -1' }}>
                <label className="form-label">Article Title <span style={{ color: '#ef4444' }}>*</span></label>
                <input
                  className="form-input"
                  value={form.title}
                  onChange={e => f('title', e.target.value)}
                  placeholder="e.g. How to generate a license key"
                  data-testid="help-kb-form-title"
                />
              </div>

              {/* content full width + preview toggle */}
              <div style={{ gridColumn: '1 / -1' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                  <label className="form-label" style={{ margin: 0 }}>Content (Markdown)</label>
                  <button
                    type="button"
                    onClick={() => setPreview(p => !p)}
                    style={{ fontSize: '0.75rem', background: preview ? '#dbeafe' : '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 6, padding: '3px 10px', cursor: 'pointer', color: preview ? '#1d4ed8' : '#64748b', display: 'flex', alignItems: 'center', gap: 4 }}
                  >
                    {preview ? <><EyeOff size={11} /> Edit</> : <><Eye size={11} /> Preview</>}
                  </button>
                </div>
                {preview ? (
                  <div style={{ minHeight: 180, border: '1px solid #e2e8f0', borderRadius: 8, padding: '12px 14px', background: '#f8fafc', overflow: 'auto' }}>
                    <MiniPreview text={form.content} />
                  </div>
                ) : (
                  <textarea
                    className="form-input"
                    value={form.content}
                    onChange={e => f('content', e.target.value)}
                    rows={8}
                    placeholder="Write article content in Markdown…&#10;&#10;Use **bold**, _italic_, `code`, ## headings, - bullets"
                    style={{ resize: 'vertical', fontFamily: 'Source Code Pro, monospace', fontSize: '0.83rem' }}
                    data-testid="help-kb-form-content"
                  />
                )}
              </div>

              {/* tags */}
              <div>
                <label className="form-label">Tags (comma-separated)</label>
                <div style={{ position: 'relative' }}>
                  <Tag size={12} color="#94a3b8" style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)' }} />
                  <input
                    className="form-input"
                    value={form.tags.join(', ')}
                    onChange={e => f('tags', e.target.value.split(',').map(t => t.trim()).filter(Boolean))}
                    placeholder="e.g. license, setup, admin"
                    style={{ paddingLeft: 26 }}
                    data-testid="help-kb-form-tags"
                  />
                </div>
              </div>

              {/* sort order */}
              <div>
                <label className="form-label">Sort Order</label>
                <input className="form-input" type="number" value={form.sortOrder} onChange={e => f('sortOrder', parseInt(e.target.value) || 0)} placeholder="0" data-testid="help-kb-form-sort" />
              </div>

              {/* publish toggle */}
              <div style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  type="checkbox"
                  id="chk-publish"
                  checked={!!form.isPublished}
                  onChange={e => f('isPublished', e.target.checked)}
                  data-testid="help-kb-form-publish"
                />
                <label htmlFor="chk-publish" style={{ fontSize: '0.86rem', color: '#334155', cursor: 'pointer' }}>
                  Publish immediately (visible to help viewers)
                </label>
              </div>
            </div>

            {/* form footer */}
            <div style={{ padding: '12px 20px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: 8, flexShrink: 0 }}>
              <button className="erp-btn" onClick={() => { setShowForm(false); setEditing(null); }} disabled={saving} style={{ fontSize: '0.83rem' }}>
                Cancel
              </button>
              <button
                className="erp-btn"
                onClick={save}
                disabled={saving}
                style={{ fontSize: '0.83rem', background: '#2563eb', color: '#fff', border: 'none' }}
                data-testid="help-kb-form-save"
              >
                <Save size={13} /> {saving ? 'Saving…' : (editing ? 'Update Article' : 'Create Article')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Article Card ─────────────────────────────────────────────────────────────
function ArticleCard({ article: a, onEdit, onDelete, onTogglePublish }) {
  const catColors = {
    'Getting Started': '#10b981', 'How To': '#3b82f6', 'Troubleshooting': '#ef4444',
    'Configuration': '#8b5cf6', 'FAQ': '#f59e0b', 'Reference': '#06b6d4',
    'Release Notes': '#64748b', 'General': '#94a3b8',
  };
  const catColor = catColors[a.category] || '#94a3b8';

  return (
    <div
      style={{
        background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10,
        padding: 16, display: 'flex', flexDirection: 'column', gap: 8,
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)', transition: 'box-shadow 0.15s',
      }}
      data-testid={`help-kb-card-${a.articleId}`}
    >
      {/* top row: screen + category */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
        <span style={{ fontSize: '0.70rem', padding: '2px 8px', borderRadius: 20, background: '#eff6ff', color: '#1d4ed8', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 3 }}>
          <Monitor size={10} /> {screenLabel(a.screenId)}
        </span>
        <span style={{ fontSize: '0.70rem', padding: '2px 8px', borderRadius: 20, background: `${catColor}18`, color: catColor, fontWeight: 600 }}>
          {a.category}
        </span>
        <span
          style={{ marginLeft: 'auto', fontSize: '0.70rem', padding: '2px 8px', borderRadius: 20, background: a.isPublished ? '#d1fae5' : '#fef9c3', color: a.isPublished ? '#065f46' : '#92400e', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 3 }}
          onClick={onTogglePublish}
          data-testid={`help-kb-publish-toggle-${a.articleId}`}
        >
          {a.isPublished ? <><CheckCircle2 size={10} /> Published</> : <><Circle size={10} /> Draft</>}
        </span>
      </div>

      {/* title */}
      <div style={{ fontWeight: 700, fontSize: '0.90rem', color: '#0f172a', lineHeight: 1.3 }}>{a.title}</div>

      {/* content preview */}
      <div style={{ fontSize: '0.78rem', color: '#64748b', lineHeight: 1.5, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical' }}>
        {(a.content || '').replace(/[#*_`>]/g, '').slice(0, 200)}
      </div>

      {/* tags */}
      {a.tags?.length > 0 && (
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {a.tags.slice(0, 4).map(t => (
            <span key={t} style={{ fontSize: '0.68rem', padding: '1px 7px', borderRadius: 20, background: '#f1f5f9', color: '#475569' }}>{t}</span>
          ))}
        </div>
      )}

      {/* actions */}
      <div style={{ display: 'flex', gap: 6, marginTop: 4, paddingTop: 8, borderTop: '1px solid #f1f5f9' }}>
        <button className="erp-btn" onClick={onEdit} style={{ fontSize: '0.75rem', flex: 1 }} data-testid={`help-kb-edit-${a.articleId}`}>
          <Edit2 size={11} /> Edit
        </button>
        <button className="erp-btn" onClick={onTogglePublish} style={{ fontSize: '0.75rem' }} data-testid={`help-kb-toggle-${a.articleId}`}>
          {a.isPublished ? <EyeOff size={11} /> : <Eye size={11} />}
        </button>
        <button className="erp-btn" onClick={onDelete} style={{ fontSize: '0.75rem', color: '#ef4444' }} data-testid={`help-kb-delete-${a.articleId}`}>
          <Trash2 size={11} />
        </button>
      </div>
    </div>
  );
}
